import { useState } from 'react';
import { concerts, type Concert } from '@/mocks/concerts';
import TicketBookingModal from './TicketBookingModal';

const STATUS_STYLES: Record<Concert['status'], string> = {
  'En cours': 'bg-accent-500 text-foreground-950',
  'À venir': 'bg-primary-500 text-foreground-950',
  Bientôt: 'bg-secondary-500 text-foreground-950',
  Terminé: 'bg-background-200 text-foreground-400',
};

function formatDate(dateStr: string): string {
  const d = new Date(`${dateStr}T00:00:00`);
  return d.toLocaleDateString('fr-FR', {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });
}

function ConcertSection() {
  const [filter, setFilter] = useState<'Tous' | Concert['status']>('Tous');
  const [selectedConcert, setSelectedConcert] = useState<Concert | null>(null);

  const filters: Array<'Tous' | Concert['status']> = [
    'Tous',
    'En cours',
    'À venir',
    'Bientôt',
    'Terminé',
  ];

  const filtered =
    filter === 'Tous' ? concerts : concerts.filter((c) => c.status === filter);

  return (
    <div>
      {/* Filtres */}
      <div className="flex items-center gap-2 overflow-x-auto no-scrollbar mb-6">
        {filters.map((f) => (
          <button
            key={f}
            onClick={() => setFilter(f)}
            className={`px-3 py-1.5 rounded-full text-xs font-medium whitespace-nowrap transition-colors ${
              filter === f
                ? 'bg-accent-500 text-foreground-950'
                : 'bg-background-50 text-foreground-400 hover:text-foreground-50 border border-background-300/60'
            }`}
          >
            {f}
          </button>
        ))}
      </div>

      {/* Grille */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
        {filtered.map((concert) => (
          <div
            key={concert.id}
            className="group rounded-xl overflow-hidden border border-background-200/70 bg-background-50 hover:border-background-300/70 transition-colors flex flex-col"
          >
            {/* Affiche */}
            <div className="relative aspect-[3/4] overflow-hidden">
              <img
                src={concert.poster_url}
                alt={`${concert.artist_name} — ${concert.title}`}
                title={`${concert.artist_name} — ${concert.title}`}
                className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent" />
              <span
                className={`absolute top-3 left-3 inline-block px-2.5 py-1 rounded-full text-xs font-semibold ${STATUS_STYLES[concert.status]}`}
              >
                {concert.status}
              </span>
              <span className="absolute top-3 right-3 inline-block px-2.5 py-1 rounded-full bg-background-950/50 backdrop-blur-sm text-xs font-semibold text-foreground-50">
                {concert.genre}
              </span>
              <div className="absolute bottom-3 left-3 right-3">
                <h3 className="font-heading font-bold text-lg text-foreground-50 leading-tight">
                  {concert.artist_name}
                </h3>
                <p className="text-sm text-foreground-300">{concert.title}</p>
              </div>
            </div>

            {/* Détails */}
            <div className="p-4 flex-1 flex flex-col">
              <div className="flex items-center gap-2 text-sm text-foreground-400 mb-1">
                <i className="ri-calendar-line text-accent-400" />
                <span className="capitalize">{formatDate(concert.date)}</span>
              </div>
              <div className="flex items-center gap-2 text-sm text-foreground-400 mb-3">
                <i className="ri-map-pin-line text-accent-400" />
                <span>
                  {concert.venue} · {concert.location}
                </span>
              </div>
              <div className="mt-auto flex items-center justify-between">
                <span className="text-sm font-semibold text-foreground-50">
                  {concert.price === 'Gratuit' ? (
                    <span className="text-accent-400">{concert.price}</span>
                  ) : (
                    concert.price
                  )}
                </span>
                {concert.status === 'Terminé' ? (
                  <button
                    disabled
                    className="inline-flex items-center gap-1.5 px-3 py-2 rounded-md bg-background-200 text-foreground-400 font-semibold text-sm cursor-not-allowed whitespace-nowrap"
                  >
                    <i className="ri-close-circle-line" />
                    Terminé
                  </button>
                ) : (
                  <button
                    onClick={() => setSelectedConcert(concert)}
                    className="inline-flex items-center gap-1.5 px-3 py-2 rounded-md bg-primary-500 text-foreground-950 font-semibold text-sm hover:bg-primary-400 transition-colors whitespace-nowrap"
                  >
                    <i className="ri-ticket-2-line" />
                    Réserver
                  </button>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>

      <TicketBookingModal
        concert={selectedConcert}
        open={selectedConcert !== null}
        onClose={() => setSelectedConcert(null)}
      />
    </div>
  );
}

export default ConcertSection;